"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { 
  authenticateWithEmail,
  verifyOTPServer,
  getUserProfile,
  logout as logoutAction
} from '../actions/auth'
import { UserProfile } from '../models/_auth_models'
import { isTokenInvalid, extractErrorMessage, logErrorDetails, isAuthError } from '../utils'
import { decodeJWT } from '../utils'

interface AuthContextType {
  user: UserProfile | null
  isAuthenticated: boolean
  isLoading: boolean
  isLoginLoading: boolean
  login: (email: string, code: string) => Promise<boolean>
  logout: () => void
  checkAuth: () => Promise<void>
  sendVerificationCode: (email: string) => Promise<boolean>
  refreshUserProfile: () => Promise<void>; // add this
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Helper function to get client-side cookies
function getClientCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    
    if (parts.length === 2) {
      const cookieValue = parts.pop()?.split(';').shift() || null;
      return cookieValue;
    }
    
    return null;
  } catch {
    return null;
  }
}

// Helper function to set client-side cookies
function setClientCookie(name: string, value: string, days: number = 7) {
  if (typeof document === 'undefined') return;
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

// Helper function to clear client-side cookies
function clearClientCookie(name: string) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoginLoading, setIsLoginLoading] = useState(false)
  const router = useRouter()

  const handleInvalidSession = async (message = 'Your session is no longer valid. Please log in again.') => {
    try {
      await logoutAction()
      setUser(null)
      clearClientCookie('access_token')
      clearClientCookie('user')
      clearClientCookie('verification-token')
      clearClientCookie('temp_token')
      toast.error('Session expired', {
        description: message,
      })
      router.push('/')
    } catch {
      setUser(null)
      clearClientCookie('access_token')
      clearClientCookie('user')
      clearClientCookie('verification-token')
      clearClientCookie('temp_token')
      router.push('/')
    }
  }

  const checkAuth = async () => {
    try {
      const token = getClientCookie('access_token')
      
      if (token) {
        if (isTokenInvalid(token)) {
          await handleInvalidSession()
          return
        }

        const userCookie = getClientCookie('user')
        
        if (userCookie) {
          try {
            const userData = JSON.parse(userCookie) as UserProfile
            setUser(userData)
          } catch {
            await recreateUserFromToken(token)
          }
        } else {
          await recreateUserFromToken(token)
        }
      } else {
        setUser(null)
      }
    } catch {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  // Periodic and visibility-based token validation
  useEffect(() => {
    if (!user) return

    const validateSession = async () => {
      const token = getClientCookie('access_token')
      if (!token || isTokenInvalid(token)) {
        await handleInvalidSession()
      }
    }

    const interval = setInterval(validateSession, 5 * 60 * 1000)

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validateSession()
      }
    }

    window.addEventListener('focus', validateSession)
    document.addEventListener('visibilitychange', onVisibilityChange)
    
    return () => {
      clearInterval(interval)
      window.removeEventListener('focus', validateSession)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [user])

  const sendVerificationCode = async (email: string): Promise<boolean> => {
    try {
      setIsLoginLoading(true)
      const response = await authenticateWithEmail({ email })
      
      if (response.status === 'success' && response.data) {
        toast.success('Verification code sent!', {
          description: response.data.message || 'Check your email for the verification code.',
        })
        return true
      } else {
        throw new Error(response.message || 'Failed to send verification code')
      }
    } catch (error: any) {
      logErrorDetails(error, 'SendVerificationCode')
      
      const message = extractErrorMessage(error)
      
      toast.error('Verification Code Error', {
        description: message,
      })
      throw new Error(message)
    } finally {
      setIsLoginLoading(false)
    }
  }

  const login = async (email: string, code: string): Promise<boolean> => {
    try {
      setIsLoginLoading(true)
      const response = await verifyOTPServer({ otp_code: code })
      
      if (response.status === 'success' && response.data) {
        // Create user profile from the OTP verification response
        const userProfile: UserProfile = {
          id: response.data.id,
          email: response.data.email,
          fullname: response.data.fullname,
          phone: response.data.phone || '',
          phone2: null,
          kyc_status: 'pending', // Default value
          country: '',
          state: '',
          city: '',
          address: '',
          postal_code: ''
        }
        
        // Store access token in cookie
        setClientCookie('access_token', response.data.token, 7)
        
        // Store user data in cookie
        setClientCookie('user', JSON.stringify(userProfile), 7)
        
        setUser(userProfile)
        toast.success('Welcome back!', {
          description: `Successfully logged in as ${userProfile.fullname}`,
        })
        router.push('/admin/home')
        
        return true
      } else {
        throw new Error(response.message || 'Failed to verify OTP')
      }
    } catch (error: any) {
      logErrorDetails(error, 'Login')
      
      const message = extractErrorMessage(error)
      
      toast.error('Login Failed', {
        description: message,
      })
      throw new Error(message)
    } finally {
      setIsLoginLoading(false)
    }
  }

  const logout = async () => {
    try {
      await logoutAction()
      clearClientCookie('access_token')
      clearClientCookie('user')
      clearClientCookie('verification-token')
      setUser(null)
      toast.success('Logged out successfully', {
        description: 'You have been logged out of your account.',
      })
      router.push('/')
    } catch {
      toast.error('Logout failed', {
        description: 'There was an issue logging you out.',
      })
      // Fallback logout
      router.push('/')
    }
  }

  // Helper function to recreate user data from token
  const recreateUserFromToken = async (token: string) => {
    try {
      const decoded = decodeJWT(token)
      
      if (decoded && decoded.email) {
        const userData: UserProfile = {
          id: decoded.sub || decoded.id || 'unknown',
          email: decoded.email,
          fullname: decoded.fullname || decoded.name || decoded.email.split('@')[0],
          phone: decoded.phone || '',
          phone2: null,
          kyc_status: 'pending',
          country: '',
          state: '',
          city: '',
          address: '',
          postal_code: ''
        }
        setUser(userData)
        try {
          setClientCookie('user', JSON.stringify(userData), 7)
        } catch {
          // Cookie set failed
        }
      } else {
        setUser(null)
        await handleInvalidSession()
      }
    } catch {
      setUser(null)
      await handleInvalidSession()
    }
  }

  const refreshUserProfile = async () => {
    try {
      const token = getClientCookie('access_token');
      if (!token || isTokenInvalid(token)) {
        await handleInvalidSession();
        return;
      }
      const profile = await getUserProfile(token);
      setUser(profile);
      setClientCookie('user', JSON.stringify(profile), 7);
    } catch (error) {
      if (isAuthError(error)) {
        await handleInvalidSession(extractErrorMessage(error));
      }
    }
  };

  useEffect(() => {
    checkAuth()
  }, [])

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isLoginLoading,
    login,
    logout,
    checkAuth,
    sendVerificationCode,
    refreshUserProfile // add this
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 